// Writers shared by the Reader's document-level features (fill identity, image fields, data links, events): page annotation lists,
// the AcroForm field list, content appended to a page, the Info dictionary and the XMP packet.

import { Dict, PName, PRef, Stream, type PObj } from "./parse";
import { serialize } from "./writer";
import { pdfText } from "./form";
import type { UpdateCtx } from "./incremental";

/** Adds `refs` to the page's /Annots (a direct array, or an indirect one that is rewritten). */
export function addPageAnnots(c: UpdateCtx, pageIndex: number, refs: PRef[]): void {
    const pref = c.pageRef(pageIndex);
    if (!pref || !refs.length) return;
    const raw = c.copy(pref.num).raw("Annots");
    const list = refs.map((r) => `${r.num} ${r.gen} R`).join(" ");
    if (raw instanceof PRef) {
        const arr = c.doc.xref.fetch(raw);
        c.writeObj(raw.num, `[${Array.isArray(arr) ? arr.map((x) => serialize(x)).join(" ") : ""} ${list}]`, raw.gen);
        return;
    }
    const d = c.copy(pref.num);
    d.set("Annots", [...(Array.isArray(raw) ? raw : []), ...refs] as PObj[]);
    c.put(pref.num, d);
}

/** Adds top-level fields to /AcroForm /Fields; makes the AcroForm when the file has none. */
export function addAcroFields(c: UpdateCtx, refs: PRef[]): void {
    if (!refs.length) return;
    const xref = c.doc.xref;
    const catNow = c.copy(c.root.num);
    const afRaw = catNow.raw("AcroForm");
    const af = catNow.dict("AcroForm");
    const keep = new Dict(xref);
    if (af) for (const k of af.keys()) if (k !== "Fields") keep.set(k, af.raw(k) as PObj);
    keep.set("Fields", [...(af?.arr("Fields") ?? []), ...refs] as PObj[]);
    if (!keep.has("DA")) keep.set("DA", "/Helv 0 Tf 0 g");
    if (!keep.has("DR")) keep.set("DR", parseInline("<< /Font << /Helv << /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >> >> >>"));
    if (afRaw instanceof PRef) c.put(afRaw.num, keep);
    else {
        const cat = c.copy(c.root.num);
        cat.set("AcroForm", keep);
        c.put(c.root.num, cat);
    }
}

/** A tiny dictionary literal as an object (only names, dictionaries and numbers). */
export function parseInline(src: string): PObj {
    let i = 0;
    const ws = () => { while (i < src.length && /\s/.test(src[i]!)) i++; };
    const val = (): PObj => {
        ws();
        if (src.startsWith("<<", i)) {
            i += 2;
            const d = new Dict(null);
            for (;;) { ws(); if (src.startsWith(">>", i)) { i += 2; return d; } const k = val() as PName; d.set(k.name, val()); }
        }
        if (src[i] === "/") { let j = ++i; while (j < src.length && !/[\s/<>\[\]]/.test(src[j]!)) j++; const n = src.slice(i, j); i = j; return new PName(n); }
        if (src[i] === "[") { i++; const a: PObj[] = []; for (;;) { ws(); if (src[i] === "]") { i++; return a; } a.push(val()); } }
        let j = i; while (j < src.length && !/[\s/<>\[\]]/.test(src[j]!)) j++;
        const t = src.slice(i, j); i = j;
        const ref = /^\s+(\d+)\s+R(?![A-Za-z])/.exec(src.slice(i));
        if (ref && /^\d+$/.test(t)) { i += ref[0].length; return new PRef(Number(t), Number(ref[1])); }
        return t === "true" ? true : t === "false" ? false : Number(t);
    };
    return val();
}

/** Appends a content stream to a page, with extra named resources in the given categories (`{ Font: { F1: "<< ... >>" } }` as source text). */
export async function appendPageContent(c: UpdateCtx, pageIndex: number, content: string, resources: Record<string, Record<string, string>> = {}): Promise<void> {
    const pref = c.pageRef(pageIndex);
    if (!pref) return;
    const page = c.doc.page(pageIndex);
    const n = c.newRef();
    await c.writeStream(n, "", new TextEncoder().encode(content));
    const d = c.copy(pref.num);
    const cur = d.raw("Contents");
    d.set("Contents", [...(Array.isArray(cur) ? cur : cur !== undefined && cur !== null ? [cur] : []), new PRef(n, 0)] as PObj[]);
    if (Object.keys(resources).length) {
        const res = new Dict(null);
        for (const k of page.resources.keys()) res.set(k, page.resources.raw(k) as PObj);
        for (const [cat, items] of Object.entries(resources)) {
            const sub = new Dict(null);
            const old = page.resources.dict(cat);
            if (old) for (const k of old.keys()) sub.set(k, old.raw(k) as PObj);
            for (const [name, src] of Object.entries(items)) sub.set(name, parseInline(src));
            res.set(cat, sub);
        }
        d.set("Resources", res);
    }
    c.put(pref.num, d);
}

/** Sets (or with "" removes) entries of the document information dictionary. */
export function setInfo(c: UpdateCtx, pairs: Array<[string, string]>): void {
    const n = c.ensureInfo();
    const d = c.copy(n);
    for (const [k, v] of pairs) { if (v) d.set(k, pdfText(v)); else d.map.delete(k); }
    c.put(n, d);
}

/** The XMP packet of the catalog as text ("" when there is none). */
export function readXmp(c: UpdateCtx): string {
    const m = c.doc.catalog.get("Metadata");
    return m instanceof Stream ? new TextDecoder("utf-8").decode(m.bytes()) : "";
}

export async function writeXmp(c: UpdateCtx, xmp: string): Promise<void> {
    const n = c.newRef();
    await c.writeStream(n, "/Type /Metadata /Subtype /XML", new TextEncoder().encode(xmp), false);
    const cat = c.copy(c.root.num);
    cat.set("Metadata", new PRef(n, 0));
    c.put(c.root.num, cat);
}
